"use client";

import { useEffect, useState, type CSSProperties } from "react";
import Link from "next/link";
import { Breadcrumb } from "@/components/ui/molecules/breadcrumb";
import { useNoteNotation } from "@/hooks/use-notation";
import { useOpenPiece } from "@/hooks/use-open-piece";
import {
  getPiece,
  type CatalogPiece,
  type CatalogPieceDetail,
} from "@/lib/api/api-client";
import {
  GESTURE_TIERS,
  WORK_SIZE_LABELS,
  gestureTier,
  workSize,
} from "@/lib/library/difficulty-tiers";
import { LIBRARY_BASE_PATH } from "@/lib/library/library-routes";
import { pieceTitleLine } from "@/lib/library/piece-metadata";
import { pieceRightsDetail, pieceRightsLine } from "@/lib/library/piece-rights";
import {
  formatDuration,
  parseCatalogTonality,
  pieceDisplayArtist,
} from "@/lib/library/piece-format";
import { tonalityLabel } from "@/lib/music/tonality";
import { documentTitle } from "@/lib/site";
import { usePiecePreview, type PreviewStatus } from "@/hooks/use-piece-preview";
import { PlayIcon } from "./library-icons";
import { PieceSheetActions } from "./piece-sheet-actions";
import { PieceSilhouette } from "./piece-silhouette";

/**
 * La fiche d'un morceau, en pleine largeur.
 *
 * # Ce que la fiche doit faire, et que l'aperçu ne fait pas
 *
 * Il y a deux écrans de présentation d'un morceau dans le produit, et ils ne
 * répondent pas à la même question. `PieceOverviewOverlay` **célèbre** un
 * morceau qu'on vient de charger : il a le score sous la main, donc il montre
 * la tessiture, la cadence de référence, la difficulté mesure par mesure. La
 * fiche, elle, doit faire **reconnaître** un morceau qu'on n'a pas ouvert, à
 * partir des seules métadonnées du catalogue.
 *
 * La ligne de partage est nette et doit le rester : rien de ce qui demande le
 * morceau chargé ne remonte ici. Le luxe de la fiche n'est pas l'ornement,
 * c'est l'espace mesuré.
 *
 * # Deux colonnes, et pourquoi
 *
 * La figure occupe la gauche, tout le texte la droite — titre, note éditoriale,
 * geste et chantier, bouton. Ce n'est pas une préférence esthétique : c'est ce
 * qui permet de donner à la silhouette les **proportions de sa grille** (elle
 * était écrasée dans un cadre deux fois trop large, avec 11 px de jeu
 * horizontal contre 1 px de vertical, donc lue en rangées de pointillés) sans
 * repousser le bouton sous la ligne de flottaison. Empilés, les deux ne
 * tiennent pas ; côte à côte, si.
 *
 * Les paliers vivent dans `globals.css`, en requêtes de **conteneur** : la
 * sidebar de la bibliothèque change la largeur disponible à viewport constant.
 *
 * # Ce qui a été retiré, et pourquoi
 *
 *  - **les six cases de métadonnées** — six fonds et six bordures pour six
 *    faits. `piece-overview/stats/piece-stats.tsx` tranche l'inverse et le dit
 *    en toutes lettres : « No cards, no borders ». Le plus bel écran du site est
 *    beau parce qu'il a retiré les boîtes ;
 *  - **l'eyebrow de genre** — « Classique » sur 96 pièces sur 100, dans
 *    l'emplacement typographique le plus fort de la page. Remplacé par
 *    `shelfLabel`, qui varie et qui situe ;
 *  - **la jauge de difficulté** — cinq pastilles dont une allumée *sont* le
 *    chiffre que `difficulty-tiers.ts` interdit d'afficher, à côté du libellé
 *    qui le dit déjà en mots ;
 *  - **le nombre de notes** — personne ne choisit un morceau au nombre de
 *    notes ; le chantier porte l'information en unité vécue ;
 *  - **le pied de fiche** (arrangeur, description, « partition disponible ») —
 *    conditionné à trois champs vrais sur zéro pièce du catalogue mesuré.
 */

/**
 * Le repère de dessin de la silhouette, et surtout ses proportions : ce sont
 * celles de la grille servie (28 × 21). Une maille carrée, donc, et un jeu
 * identique dans les deux directions.
 *
 * L'ancien cadre large (720 × 334) achetait la position du bouton au prix de la
 * lisibilité de la figure. La mise en page à deux colonnes rend cet arbitrage
 * caduc : le bouton est haut *et* la figure est juste.
 *
 * Les nombres eux-mêmes ne sont qu'un rapport — le conteneur reprend
 * `aspectRatio` et la figure se met à la largeur disponible.
 */
/** Le bandeau de la fiche large : 4:1, le rapport de la grille 112 × 28. */
const FIGURE_WIDTH = 1120;
const FIGURE_HEIGHT = 280;
/**
 * Le palier étroit : un bandeau lui aussi, mais moins allongé — 56 × 24,
 * rapport 2,33:1. Le bandeau de la fiche large (4:1) ferait 97 px de haut à
 * 390 px, avec des mailles de 3,5 px.
 */
const NARROW_FIGURE_WIDTH = 560;
const NARROW_FIGURE_HEIGHT = 240;

/**
 * Taille du titre selon sa longueur, en caractères. Bornes hautes.
 *
 * 44 px est la taille de référence ; au-delà, un titre comme « Toccata et fugue
 * en ré mineur » déborderait de la colonne de droite ou s'y casserait en trois
 * lignes. La fiche reste sous 48 px : l'aperçu monte à 56–96, on rejoint la
 * famille typographique sans marcher dessus.
 */
const TITLE_SIZE_BOUNDS: readonly (readonly [number, number])[] = [
  [32, 44],
  [48, 36],
];
const TITLE_SIZE_MIN = 30;

function titleSize(title: string): number {
  for (const [maxLength, size] of TITLE_SIZE_BOUNDS) {
    if (title.length <= maxLength) {
      return size;
    }
  }

  return TITLE_SIZE_MIN;
}

/** Le gabarit de page, partagé par la fiche, l'attente et l'échec. */
/** Le gabarit de page, partagé par la fiche, l'attente et l'échec. */
const PAGE_CLASS =
  "piece-sheet mx-auto w-full max-w-[1000px] px-8 py-8 xl:px-10 xl:py-10";

type Props = {
  pieceId: string;
  /**
   * La fiche telle que le serveur l'a lue, ou `null` s'il n'a pas pu.
   *
   * C'est ce qui met le titre, le compositeur et les chiffres dans le HTML
   * servi. Le repli client reste en place pour le cas où l'API n'a pas répondu
   * au moment du rendu : la page s'affiche alors comme avant, en chargeant
   * elle-même.
   */
  initialDetail: CatalogPieceDetail | null;
};

export function PieceSheet({ pieceId, initialDetail }: Props) {
  /*
    Ce que le client a dû aller chercher lui-même, quand le serveur n'a rien
    livré. La fiche affichée est dérivée de la prop, pas copiée dans un état :
    l'état survivrait à une navigation d'un morceau au suivant — même position
    dans l'arbre, donc pas de remontage — et la fiche montrerait le morceau
    précédent avec les chiffres du nouveau.
  */
  const [clientDetail, setClientDetail] = useState<CatalogPieceDetail | null>(
    null,
  );
  const [failed, setFailed] = useState(false);
  const detail = initialDetail ?? clientDetail;
  const { openPiece, launching, error: launchError } = useOpenPiece();
  const notation = useNoteNotation();
  // En tête, avec les autres : la fiche a deux sorties anticipées — l'échec et
  // l'attente — et un hook appelé après elles change le nombre de hooks d'un
  // rendu à l'autre. React s'en plaint et cesse de rendre la page. Il prend
  // `pieceId`, la prop, pas `detail.piece.id`, qui n'existe pas encore ici.
  const preview = usePiecePreview(pieceId);

  useEffect(() => {
    if (initialDetail !== null) {
      return;
    }

    let cancelled = false;
    setClientDetail(null);
    setFailed(false);

    getPiece(pieceId)
      .then((value) => {
        if (!cancelled) {
          setClientDetail(value);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }

        /*
          Le message technique va à la console et nulle part ailleurs. Servi à
          l'écran, il donnait « API error: 404 Not Found » — une chaîne HTTP
          brute, en anglais, sur le seul écran où quelqu'un cherche à savoir si
          le morceau existe encore.
        */
        console.error(`Chargement de la pièce ${pieceId} :`, err);
        setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [pieceId, initialDetail]);

  /*
    Le titre de l'onglet, quand c'est le client qui a trouvé la pièce.

    `generateMetadata` s'exécute sous une session anonyme — le serveur Next n'a
    pas le jeton de la personne. Un morceau qu'on t'a partagé mais qui n'est pas
    publié y répond donc 404, et la page sortait avec « Morceau introuvable » en
    titre alors qu'elle affiche la fiche complète, chargée juste au-dessus avec
    le bon jeton. Next applique ses métadonnées au rendu et n'offre rien pour
    les corriger après coup ; le document, lui, s'écrit.

    Rien à défaire au démontage : la navigation suivante réécrit le titre, que
    ce soit par les métadonnées d'une autre route ou par cet effet.
  */
  useEffect(() => {
    if (initialDetail !== null || clientDetail === null) {
      return;
    }

    document.title = documentTitle(pieceTitleLine(clientDetail.piece));
  }, [initialDetail, clientDetail]);

  if (failed) {
    return (
      <>
        <Breadcrumb
          items={[{ label: "Bibliothèque", href: LIBRARY_BASE_PATH }]}
          current="Morceau introuvable"
        />
        <SheetError />
      </>
    );
  }

  if (detail === null) {
    return (
      <>
        <Breadcrumb
          items={[{ label: "Bibliothèque", href: LIBRARY_BASE_PATH }]}
          current="Chargement…"
        />
        <SheetSkeleton />
      </>
    );
  }

  const { piece } = detail;
  const tier = gestureTier(piece.difficulty, piece.durationSec);
  const size = workSize(piece.noteCount ?? detail.noteCount, piece.durationSec);
  const duration = formatDuration(piece.durationSec);
  const tonality = parseCatalogTonality(piece.tonality);
  const playable = detail.scoreId !== null;
  /**
   * Part du morceau que l'extrait couvre. C'est elle qui met la tête de
   * lecture et la jauge à l'échelle de la figure, laquelle décrit le morceau
   * entier — sans quoi les deux traverseraient toute la largeur en ayant joué
   * vingt secondes d'un morceau qui en dure sept minutes.
   */
  const previewCoverage =
    preview.durationMs !== null && piece.durationSec !== null && piece.durationSec > 0
      ? Math.min(1, preview.durationMs / (piece.durationSec * 1000))
      : null;
  const hasStats =
    piece.tonality !== null ||
    piece.bpm !== null ||
    duration !== null ||
    piece.measureCount > 0;
  /*
    Les droits, en clair. La fiche est le seul écran assez large pour les dire :
    une partition porte deux couches — l'œuvre et la gravure — et une carte doit
    choisir un mot là où la fiche peut rendre le crédit exigible. Voir
    `lib/library/piece-rights.ts`.
  */
  const rightsLine = pieceRightsLine(piece);
  const rightsDetail = pieceRightsDetail(piece);

  /*
    `?? null` et non une lecture directe : ces deux champs sont récents côté
    API, et une pièce servie par une version antérieure ne les porte pas du
    tout. Un champ absent doit dégrader comme un champ vide — ce qui est de
    toute façon le cas nominal, ils sont nuls sur la grande majorité du
    catalogue.
  */
  const shelfLabel = detail.shelfLabel ?? null;
  const shelfNote = detail.shelfNote ?? null;

  return (
    <>
      <Breadcrumb
        items={[{ label: "Bibliothèque", href: LIBRARY_BASE_PATH }]}
        current={piece.title}
      />

      <div
        className={`${PAGE_CLASS} piece-sheet-enter`}
        /*
          Lu par la requête de conteneur qui colle le bouton en bas sous 600 px.
          Sans arrangement jouable il n'y a pas de bouton, seulement une phrase,
          et une phrase n'a rien à suivre à l'écran.
        */
        data-playable={playable}
      >
        <div className="piece-sheet-grid">
          <div className="piece-sheet-identity">
            {shelfLabel !== null && (
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-accent-bright">
                {shelfLabel}
              </p>
            )}

            <h1
              className="piece-sheet-title"
              style={
                {
                  "--piece-title-size": `${titleSize(piece.title)}px`,
                } as CSSProperties
              }
            >
              {piece.title}
            </h1>

            <p className="mt-1.5 text-[16px] text-text-secondary">
              {pieceDisplayArtist(piece)}
            </p>
          </div>

          {/*
            La silhouette, nue et grande. Rien par-dessus : ni voile, ni titre,
            ni assombrissement. La carte vend — elle a besoin de son ancre de
            texte pour tenir dans une grille — la fiche montre.

            `showReferences` pose les bandes de territoire et les règles : sans
            elles, une pièce qui tient sur cinq touches rend une poignée de
            points dans un grand rectangle noir, ce qui se lit comme un bug
            alors que c'est l'information. Voir `piece-silhouette.tsx`.
          */}
          <div className="piece-sheet-figure">
            {/*
              Deux cadres, un seul visible — le choix est fait en CSS par une
              requête de conteneur (`.piece-sheet-figure-wide` / `-narrow`).

              Pourquoi pas une seule figure adaptative : le rapport du cadre
              doit suivre celui de la grille encodée, sans quoi les mailles
              cessent d'être carrées. Or la grille change entre les deux
              paliers — 112 × 28 en bandeau, 28 × 21 en étroit — parce qu'un
              bandeau 4:1 à 390 px de large ferait 97 px de haut, avec des
              mailles de 3,5 px.

              Et pourquoi pas un choix en JavaScript : c'est la largeur du
              *conteneur* qui décide, pas celle de la fenêtre, et elle n'est
              pas connue au rendu serveur. Un choix côté client provoquerait un
              saut à l'hydratation.
            */}
            <PieceFigure
              className="piece-sheet-figure-wide"
              silhouette={detail.silhouetteDetail ?? piece.silhouette}
              width={FIGURE_WIDTH}
              height={FIGURE_HEIGHT}
              piece={piece}
              preview={preview}
              previewCoverage={previewCoverage}
            />

            <PieceFigure
              className="piece-sheet-figure-narrow"
              silhouette={detail.silhouetteTall ?? piece.silhouette}
              width={NARROW_FIGURE_WIDTH}
              height={NARROW_FIGURE_HEIGHT}
              piece={piece}
              preview={preview}
              previewCoverage={previewCoverage}
            />

            <FigureLegend />
          </div>

          <div className="piece-sheet-body flex flex-col gap-[26px]">
            {/*
              La note éditoriale : la phrase que la carte de la sélection d'accueil
              porte déjà et que la fiche avait perdue. C'est elle qui empêche
              l'arrivée sur la destination d'être une soustraction.
            */}
            {shelfNote !== null && (
              <p
                className="text-[15px] leading-[1.6] text-text-secondary"
                style={{ maxWidth: "42ch" }}
              >
                {shelfNote}
              </p>
            )}

            {/*
              Le geste et le chantier sur une ligne. Jamais fondus — ils
              répondent à deux questions — mais la ponctuation suffit à les
              séparer : deux boîtes, c'était deux fonds et deux bordures pour
              deux mots.
            */}
            {(tier !== null || size !== null) && (
              <p className="text-[15px] leading-relaxed">
                {tier !== null && (
                  <span className="text-text-primary">
                    {GESTURE_TIERS[tier].long}
                  </span>
                )}
                {tier !== null && size !== null && (
                  <span className="text-text-muted"> · </span>
                )}
                {size !== null && (
                  <span className="text-text-secondary">
                    {WORK_SIZE_LABELS[size]}
                  </span>
                )}
              </p>
            )}

            {/*
              Ranger et partager. Dans le corps de la fiche et non dans le bloc
              du bouton : sous 600 px, ce bloc devient une barre collée en bas,
              et y empiler trois boutons mangerait le tiers de l'écran. « Ouvrir
              ce morceau » est ce qu'on est venu faire ; ranger et partager sont
              ce qu'on peut faire en plus, et ils appartiennent au texte.
            */}
            <PieceSheetActions piece={piece} />
          </div>

          <div className="piece-sheet-cta">
            {/*
              Sans arrangement, la phrase **remplace** le bouton au lieu de le
              griser : un bouton désactivé invite à cliquer puis punit.
            */}
            {playable ? (
              <button
                type="button"
                onClick={() => {
                  // L'extrait continuerait de jouer par-dessus le studio, et
                  // par-dessus le morceau qu'on vient d'y charger.
                  preview.stop();
                  void openPiece(piece.id);
                }}
                disabled={launching}
                className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl px-7 text-[14px] font-semibold text-text-primary transition-all duration-200 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100"
                style={{
                  background:
                    "linear-gradient(135deg, var(--color-accent), var(--color-accent-dim))",
                  boxShadow: "0 4px 20px rgba(139, 92, 246, 0.2)",
                }}
              >
                {launching ? <Spinner /> : <PlayIcon />}
                Ouvrir ce morceau
              </button>
            ) : (
              <p className="text-[13px] text-text-muted">
                Pas encore d&apos;arrangement jouable pour ce morceau.
              </p>
            )}

            {launchError !== null && (
              <p className="mt-2 text-[12px] text-err">{launchError}</p>
            )}
          </div>

          {/*
            Rendu seulement s'il y a quelque chose à mettre dessous : un import
            que personne n'a analysé n'a ni tonalité, ni tempo, ni durée, ni
            mesures, et le bloc se réduirait alors à un filet orphelin en pied
            de page.
          */}
          {(hasStats || rightsLine !== null || rightsDetail !== null) && (
            <div className="piece-sheet-stats">
              <div className="piece-sheet-stats-row">
                {/*
                  Repli sur la valeur brute plutôt que sur rien : une chaîne
                  qu'on ne sait pas lire reste une donnée vraie, seulement mal
                  habillée.
                */}
                {piece.tonality !== null && (
                  <Stat
                    label="Tonalité"
                    value={
                      tonality === null
                        ? piece.tonality
                        : tonalityLabel(tonality, notation)
                    }
                  />
                )}
                {piece.bpm !== null && (
                  <Stat label="Tempo" value={`${piece.bpm} BPM`} />
                )}
                {duration !== null && <Stat label="Durée" value={duration} />}
                {piece.measureCount > 0 && (
                  <Stat label="Mesures" value={String(piece.measureCount)} />
                )}
              </div>

              {/*
                En pied de fiche et non en pastille : c'est une mention légale,
                elle est due, pas exposée. La conséquence n'est écrite que
                lorsqu'elle change quelque chose — un morceau du domaine public
                n'a rien à annoncer, c'est la règle de tout le catalogue.
              */}
              {(rightsLine !== null || rightsDetail !== null) && (
                <p className="mt-5 max-w-[68ch] text-[12px] leading-relaxed text-text-muted">
                  {rightsLine}
                  {rightsLine !== null && rightsDetail !== null && <br />}
                  {rightsDetail}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Une statistique : la valeur flotte, l'étiquette la nomme. Ni fond ni bordure
 * — voir l'en-tête du fichier.
 */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="piece-sheet-stat-value">{value}</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted">
        {label}
      </div>
    </div>
  );
}

/**
 * Ce que disent les deux couleurs de la figure.
 *
 * Une légende et non une info-bulle : c'est la seule clé de lecture du dessin,
 * et une clé de lecture qu'il faut survoler pour obtenir n'en est pas une.
 */
/**
 * Le bouton d'écoute et sa jauge, posés sur la figure.
 *
 * # Tout est à l'échelle du morceau, pas de l'extrait
 *
 * L'extrait fait vingt secondes, le morceau parfois sept minutes — sur les 221
 * pièces du catalogue, aucune ne dure moins que l'extrait, et on en entend 23 %
 * en moyenne. La jauge et le compteur mesurent donc une position **dans le
 * morceau**, comme la tête de lecture sur la figure juste au-dessus.
 *
 * Le segment clair du rail dit jusqu'où l'aperçu ira. C'est ce qui explique,
 * sur une pièce longue, que la lecture s'arrête au dixième du rail : on entend
 * le début, et on le voit.
 *
 * # États
 *
 * `idle` → `loading` (le rond bat) → `playing` (jauge et compteur) → `idle`.
 * Il n'y a pas d'état d'erreur visible : l'appelant cesse de rendre le contrôle
 * quand l'extrait n'existe pas. Un aperçu est un bonus, et un bouton mort en
 * dit plus long qu'un bouton absent.
 */
function PreviewControl({
  status,
  progress,
  coverage,
  excerptMs,
  durationSec,
  onToggle,
}: {
  status: PreviewStatus;
  progress: number;
  coverage: number | null;
  excerptMs: number | null;
  durationSec: number | null;
  onToggle: () => void;
}) {
  const playing = status === "playing";
  const loading = status === "loading";
  const span = coverage ?? 1;
  const positionSec =
    excerptMs === null ? null : Math.round((progress * excerptMs) / 1000);

  return (
    <div
      className="absolute inset-x-0 bottom-0 flex items-center gap-3 px-3 pb-3 pt-10"
      style={{
        background:
          "linear-gradient(to top, rgba(6,6,12,.85) 0%, rgba(6,6,12,.55) 55%, transparent 100%)",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={playing ? "Arrêter l\u2019aperçu" : "Écouter un extrait"}
        className={`flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full transition-transform duration-150 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          loading ? "piece-preview-pulse" : ""
        }`}
        style={{
          background: "var(--color-text-primary)",
          color: "#0c0c18",
          boxShadow: "0 4px 16px rgba(0,0,0,.5)",
        }}
      >
        {playing ? <PauseGlyph /> : <PlayGlyph />}
      </button>

      <div className="flex flex-1 items-center gap-2.5">
        <div
          className="relative h-[2px] flex-1 overflow-hidden rounded-full"
          style={{ background: "rgba(255,255,255,.16)" }}
        >
          {/* Jusqu'où l'aperçu ira, sur un rail qui représente le morceau. */}
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{
              width: `${span * 100}%`,
              background: "rgba(255,255,255,.3)",
            }}
          />

          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{
              width: `${progress * span * 100}%`,
              background: "var(--color-text-primary)",
            }}
          />
        </div>

        {durationSec !== null && (
          <span
            className="shrink-0 text-[10px] tabular-nums"
            style={{
              fontFamily: "var(--font-geist-mono)",
              color: "rgba(255,255,255,.6)",
            }}
          >
            {playing && positionSec !== null
              ? `${formatDuration(positionSec)} / ${formatDuration(durationSec)}`
              : formatDuration(durationSec)}
          </span>
        )}
      </div>
    </div>
  );
}

function PlayGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="currentColor" aria-hidden="true">
      <path d="M3.5 2.2v8.6a.6.6 0 0 0 .92.5l6.8-4.3a.6.6 0 0 0 0-1l-6.8-4.3a.6.6 0 0 0-.92.5Z" />
    </svg>
  );
}

function PauseGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="currentColor" aria-hidden="true">
      <rect x="3" y="2.2" width="2.8" height="8.6" rx="0.7" />
      <rect x="7.2" y="2.2" width="2.8" height="8.6" rx="0.7" />
    </svg>
  );
}

/** Un cadre de figure : la silhouette, et le contrôle d'écoute posé dessus. */
function PieceFigure({
  className,
  silhouette,
  width,
  height,
  piece,
  preview,
  previewCoverage,
}: {
  className: string;
  silhouette: string | null;
  width: number;
  height: number;
  piece: CatalogPiece;
  preview: ReturnType<typeof usePiecePreview>;
  previewCoverage: number | null;
}) {
  return (
    <div
      className={`relative w-full overflow-hidden rounded-xl ${className}`}
      style={{
        aspectRatio: `${width} / ${height}`,
        border: "1px solid var(--color-border-subtle)",
      }}
    >
      <PieceSilhouette
        silhouette={silhouette}
        width={width}
        height={height}
        className="block h-full w-full"
        showReferences
        progress={preview.status === "playing" ? preview.progress : null}
        previewCoverage={previewCoverage}
        label={`Silhouette de « ${piece.title} » : chaque point est un groupe de notes, la main gauche en turquoise sous la main droite en violet, du début du morceau à gauche jusqu'à la fin à droite.`}
      />

      {/*
        Le contrôle d'écoute est posé *sur* la figure, comme sur une pochette,
        et pas à côté : c'est la figure qu'il anime, et l'y rattacher évite
        qu'on cherche le rapport entre un bouton de page et une image qui bouge.

        Il ne paraît pas tant que le morceau n'a pas de silhouette : un aplat nu
        n'a rien à faire balayer, et l'absence de silhouette signale justement
        un score que l'analyse n'a pas exploité.
      */}
      {piece.silhouette !== null && preview.status !== "unavailable" && (
        <PreviewControl
          status={preview.status}
          progress={preview.progress}
          coverage={previewCoverage}
          excerptMs={preview.durationMs}
          durationSec={piece.durationSec}
          onToggle={preview.toggle}
        />
      )}
    </div>
  );
}

function FigureLegend() {
  return (
    <p className="mt-3 flex items-center gap-5 font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted">
      <LegendKey color="var(--color-hand-right)" label="Main droite" />
      <LegendKey color="var(--color-hand-left)" label="Main gauche" />
    </p>
  );
}

function LegendKey({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className="h-[7px] w-[7px] rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

/**
 * L'échec de chargement.
 *
 * Une phrase et une sortie, pas un code d'erreur : à ce stade la seule question
 * de l'utilisateur est « et maintenant ? ». Le détail technique est déjà parti
 * en console.
 */
function SheetError() {
  return (
    <div className={PAGE_CLASS}>
      <h1
        className="piece-sheet-title"
        style={{ "--piece-title-size": "36px" } as CSSProperties}
      >
        Ce morceau n&apos;est plus au catalogue.
      </h1>

      <p className="mt-4 max-w-[42ch] text-[15px] leading-[1.6] text-text-secondary">
        Il a peut-être été retiré, ou le lien qui t&apos;a mené ici a vieilli.
        Le reste du répertoire n&apos;a pas bougé.
      </p>

      <Link
        href={LIBRARY_BASE_PATH}
        className="mt-6 inline-flex h-11 items-center rounded-xl px-6 text-[14px] text-text-primary transition-colors duration-150 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        style={{ border: "1px solid var(--color-border-strong)" }}
      >
        Retourner au répertoire
      </Link>
    </div>
  );
}

/**
 * L'attente, à la structure exacte de la fiche : mêmes colonnes, même rapport
 * de figure, même rangée de quatre statistiques. Un squelette qui ne réserve
 * pas la bonne place ne fait que déplacer le sursaut au moment où les données
 * arrivent.
 */
function SheetSkeleton() {
  return (
    <div
      className={`${PAGE_CLASS} animate-pulse motion-reduce:animate-none`}
      aria-hidden="true"
    >
      <div className="piece-sheet-grid">
        <div className="piece-sheet-identity">
          <div className="h-[10px] w-32 rounded bg-white/[0.04]" />
          <div className="mt-3 h-[44px] w-3/4 rounded bg-white/[0.06]" />
          <div className="mt-1.5 h-[16px] w-1/2 rounded bg-white/[0.04]" />
        </div>

        <div className="piece-sheet-figure">
          <div
            className="w-full rounded-xl bg-white/[0.03]"
            style={{ aspectRatio: `${FIGURE_WIDTH} / ${FIGURE_HEIGHT}` }}
          />
          <div className="mt-3 h-[10px] w-48 rounded bg-white/[0.03]" />
        </div>

        <div className="piece-sheet-body flex flex-col gap-[26px]">
          <div className="h-[72px] w-full max-w-[42ch] rounded bg-white/[0.03]" />
          <div className="h-[15px] w-56 rounded bg-white/[0.04]" />
        </div>

        <div className="piece-sheet-cta">
          <div className="h-11 w-52 rounded-xl bg-white/[0.05]" />
        </div>

        <div className="piece-sheet-stats">
          <div className="piece-sheet-stats-row">
            {SKELETON_STATS.map((width) => (
              <div key={width}>
                <div
                  className="h-[26px] rounded bg-white/[0.05]"
                  style={{ width }}
                />
                <div className="mt-1 h-[10px] w-16 rounded bg-white/[0.03]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Largeurs des quatre valeurs simulées. Mesurées à l'écran sur « Do majeur »,
 *  « 120 BPM », « 0:48 » et « 24 », pour que la rangée ne change pas de rythme
 *  au moment où les vraies valeurs arrivent. */
const SKELETON_STATS = [138, 107, 61, 46];

function Spinner() {
  return (
    <svg
      className="animate-spin motion-reduce:animate-none"
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M8 2a6 6 0 1 0 6 6" />
    </svg>
  );
}
